import { useConfirm } from '@/components/Confirm';
import {
  PET_SPECIES,
  type PetSpecies,
  type Vaccine,
  type VaccinePolicy,
  type VaccineServiceCategory,
  createVaccinePolicy,
  deleteVaccinePolicy,
  listVaccinePolicies,
  listVaccines,
  updateVaccinePolicy,
} from '@/lib/api';
import { cn } from '@/lib/utils';
import { Plus, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';

const CATEGORIES: VaccineServiceCategory[] = ['boarding', 'daycare', 'grooming'];
const CATEGORY_LABEL: Record<VaccineServiceCategory, string> = {
  boarding: 'Boarding',
  daycare: 'Daycare',
  grooming: 'Grooming',
};
const SPECIES_LABEL: Record<PetSpecies, { singular: string; plural: string }> = {
  dog: { singular: 'dog', plural: 'Dogs' },
  cat: { singular: 'cat', plural: 'Cats' },
};

export function parseSpecies(raw: string): PetSpecies[] {
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return PET_SPECIES.filter((sp) => parsed.includes(sp));
  } catch {
    return [];
  }
}

/**
 * Vaccine policy authoring — per pet type.
 *
 * Policies are (species, service category, vaccine): the gate only measures a
 * pet against the policies for ITS species, so a cattery's FVRCP rule never
 * blocks a dog and a kennel's DHPP rule never blocks a cat. The page shows
 * one tab per species with the three category columns beneath it. Each row:
 * vaccine name, required toggle, grace period editor, delete. The add form
 * lists only the tenant's vaccines that are given to the selected species.
 */
export function VaccinePoliciesSettings() {
  const [policies, setPolicies] = useState<VaccinePolicy[]>([]);
  const [vaccines, setVaccines] = useState<Vaccine[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [species, setSpecies] = useState<PetSpecies>('dog');
  const { confirm, ConfirmDialog } = useConfirm();
  const [newRow, setNewRow] = useState<{
    species: PetSpecies;
    serviceCategory: VaccineServiceCategory;
    vaccineId: string;
    gracePeriodDays: string;
  } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [policiesRes, vaccinesRes] = await Promise.all([
        listVaccinePolicies(undefined, true),
        listVaccines(true),
      ]);
      setPolicies(policiesRes.policies);
      setVaccines(vaccinesRes.vaccines);
    } catch (e) {
      setError((e as Error).message ?? 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function handleToggleRequired(p: VaccinePolicy) {
    try {
      await updateVaccinePolicy(p.id, { required: p.required !== 1 });
      await load();
    } catch (e) {
      setError((e as Error).message);
    }
  }

  async function handleGraceChange(p: VaccinePolicy, days: number) {
    try {
      await updateVaccinePolicy(p.id, { gracePeriodDays: days });
      await load();
    } catch (e) {
      setError((e as Error).message);
    }
  }

  async function handleDelete(id: string) {
    const yes = await confirm({
      title: 'Delete vaccine policy?',
      message: 'Pets with vaccinations on file are unaffected.',
      confirmLabel: 'Delete',
      danger: true,
    });
    if (!yes) return;
    try {
      await deleteVaccinePolicy(id);
      await load();
    } catch (e) {
      setError((e as Error).message);
    }
  }

  async function handleCreate() {
    if (!newRow) return;
    if (!newRow.vaccineId) {
      setError('Select a vaccine from the list');
      return;
    }
    try {
      await createVaccinePolicy({
        species: newRow.species,
        serviceCategory: newRow.serviceCategory,
        vaccineId: newRow.vaccineId,
        required: true,
        gracePeriodDays: Number(newRow.gracePeriodDays) || 0,
      });
      setNewRow(null);
      await load();
    } catch (e) {
      setError((e as Error).message);
    }
  }

  // Build a map from vaccineId to Vaccine for label lookups.
  const vaccineMap = new Map(vaccines.map((v) => [v.id, v]));
  const vaccinesForSpecies = (sp: PetSpecies) =>
    vaccines.filter((v) => parseSpecies(v.speciesAllowed).includes(sp));

  // Policies for the selected species, grouped by category. Rows whose
  // species is outside the closed set (legacy data) are not shown.
  const byCategory: Record<VaccineServiceCategory, VaccinePolicy[]> = {
    boarding: [],
    daycare: [],
    grooming: [],
  };
  for (const p of policies) {
    if (p.species === species && byCategory[p.serviceCategory]) {
      byCategory[p.serviceCategory].push(p);
    }
  }
  const requiredCount = (sp: PetSpecies) =>
    policies.filter((p) => p.species === sp && p.required === 1).length;

  // Vaccines for this species not already in a (species, category) policy.
  function availableVaccinesForCategory(cat: VaccineServiceCategory): Vaccine[] {
    const existingIds = new Set(byCategory[cat].map((p) => p.vaccineId));
    return vaccinesForSpecies(species).filter((v) => !existingIds.has(v.id));
  }

  const speciesVaccines = vaccinesForSpecies(species);
  const label = SPECIES_LABEL[species];

  return (
    <div className="space-y-6">
      <ConfirmDialog />
      <header>
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
          <h1 className="font-heading text-2xl font-medium tracking-tight">Vaccine policies</h1>
          <NavLink
            to="/vaccine-overrides"
            className="text-xs font-medium text-primary hover:underline"
          >
            View overrides queue
          </NavLink>
        </div>
        <p className="mt-1 text-sm text-muted-foreground">
          Which vaccines each type of pet needs for each service, and how much grace beyond expiry
          the policy gate tolerates. A pet is only checked against the policies for its own species.
        </p>
        {vaccines.length === 0 && !loading && (
          <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            No vaccines are configured yet.{' '}
            <NavLink to="/settings/vaccines" className="font-medium underline underline-offset-2">
              Set up your vaccine catalogue
            </NavLink>{' '}
            before adding policies.
          </div>
        )}
      </header>

      {error && (
        <div className="rounded-lg bg-destructive/10 text-destructive px-4 py-3 mb-4 text-sm">
          {error}
        </div>
      )}

      {/* Species tabs */}
      <div
        role="tablist"
        aria-label="Pet type"
        className="flex gap-1 overflow-x-auto rounded-lg bg-secondary/50 p-1"
      >
        {PET_SPECIES.map((sp) => {
          const active = sp === species;
          const n = requiredCount(sp);
          return (
            <button
              key={sp}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => {
                setSpecies(sp);
                setNewRow(null);
              }}
              className={cn(
                'flex min-h-10 flex-1 items-center justify-center gap-2 rounded-md px-3 text-sm font-medium transition-colors press',
                active
                  ? 'bg-surface text-foreground shadow-sm ring-1 ring-border'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {SPECIES_LABEL[sp].plural}
              <span
                className={cn(
                  'num rounded-full px-1.5 py-0.5 text-[10px]',
                  n > 0 ? 'bg-primary/10 text-primary' : 'bg-secondary text-muted-foreground',
                )}
                aria-label={`${n} required`}
              >
                {n}
              </span>
            </button>
          );
        })}
      </div>

      {loading ? (
        <p className="text-muted-foreground text-sm">Loading…</p>
      ) : (
        <div className="space-y-4">
          {vaccines.length > 0 && speciesVaccines.length === 0 && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
              None of your vaccines are given to {label.singular}s. Add one, or tick {label.plural}{' '}
              on an existing vaccine, under{' '}
              <NavLink to="/settings/vaccines" className="font-medium underline underline-offset-2">
                Vaccines
              </NavLink>
              . Loading the standard library adds the usual {label.singular} vaccines with default
              policies.
            </div>
          )}
          <div className="grid gap-6 md:grid-cols-3">
            {CATEGORIES.map((cat) => {
              const available = availableVaccinesForCategory(cat);
              return (
                <section
                  key={cat}
                  className="rounded-xl border border-border bg-card"
                  aria-label={`${CATEGORY_LABEL[cat]} policies`}
                >
                  <header className="px-4 py-3 border-b border-border flex items-center justify-between gap-2">
                    <h2 className="font-medium">
                      {CATEGORY_LABEL[cat]}{' '}
                      <span className="text-xs font-normal text-muted-foreground">
                        · {label.plural.toLowerCase()}
                      </span>
                    </h2>
                    <button
                      type="button"
                      className="flex min-h-9 items-center gap-1 rounded-md px-2 text-xs text-primary disabled:cursor-not-allowed disabled:opacity-50"
                      disabled={available.length === 0}
                      title={
                        available.length === 0
                          ? speciesVaccines.length === 0
                            ? `No ${label.singular} vaccines in your catalogue`
                            : 'All vaccines already added for this category'
                          : undefined
                      }
                      onClick={() =>
                        setNewRow({
                          species,
                          serviceCategory: cat,
                          vaccineId: available[0]?.id ?? '',
                          gracePeriodDays: '0',
                        })
                      }
                    >
                      <Plus className="size-3" /> Add
                    </button>
                  </header>
                  <ul className="divide-y divide-border">
                    {newRow && newRow.serviceCategory === cat && newRow.species === species && (
                      <li className="px-4 py-3 space-y-2 bg-secondary/30">
                        <div className="space-y-1">
                          <label className="text-xs font-medium text-muted-foreground">
                            Vaccine
                          </label>
                          <select
                            className="w-full px-2 py-1.5 text-sm border border-border rounded bg-background"
                            value={newRow.vaccineId}
                            onChange={(e) => setNewRow({ ...newRow, vaccineId: e.target.value })}
                          >
                            <option value="">— Select vaccine —</option>
                            {available.map((v) => (
                              <option key={v.id} value={v.id}>
                                {v.name}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="flex items-center gap-2">
                          <label className="text-xs text-muted-foreground">Grace days</label>
                          <input
                            type="number"
                            min={0}
                            className="w-20 px-2 py-1 text-sm border border-border rounded bg-background"
                            value={newRow.gracePeriodDays}
                            onChange={(e) =>
                              setNewRow({ ...newRow, gracePeriodDays: e.target.value })
                            }
                          />
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <button
                            type="button"
                            className="min-h-9 text-xs bg-primary text-primary-foreground rounded px-3 py-1"
                            onClick={handleCreate}
                          >
                            Save
                          </button>
                          <button
                            type="button"
                            className="min-h-9 px-2 text-xs text-muted-foreground"
                            onClick={() => setNewRow(null)}
                          >
                            Cancel
                          </button>
                        </div>
                      </li>
                    )}
                    {byCategory[cat].length === 0 &&
                    (!newRow || newRow.serviceCategory !== cat || newRow.species !== species) ? (
                      <li className="px-4 py-6 text-xs text-muted-foreground text-center">
                        No {label.singular} policies. {label.plural} are not gated for{' '}
                        {CATEGORY_LABEL[cat].toLowerCase()} until you add one.
                      </li>
                    ) : (
                      byCategory[cat].map((p) => {
                        const vax = vaccineMap.get(p.vaccineId);
                        return (
                          <li key={p.id} className="px-4 py-3 flex items-center gap-2">
                            <div className="flex-1 min-w-0">
                              <div
                                className="text-sm font-medium truncate"
                                title={vax?.name ?? p.vaccineId}
                              >
                                {vax?.name ?? (
                                  <span className="font-mono text-xs">{p.vaccineId}</span>
                                )}
                              </div>
                              <div className="text-xs text-muted-foreground mt-0.5">
                                {p.required === 1 ? 'Required' : 'Advisory'} · grace{' '}
                                <input
                                  type="number"
                                  min={0}
                                  className="w-12 px-1 py-0 text-xs border border-border rounded bg-background"
                                  value={p.gracePeriodDays}
                                  aria-label={`Grace days for ${vax?.name ?? p.vaccineId}`}
                                  onChange={(e) =>
                                    void handleGraceChange(p, Number(e.target.value) || 0)
                                  }
                                />{' '}
                                days
                              </div>
                            </div>
                            <button
                              type="button"
                              className="min-h-9 shrink-0 px-1 text-xs text-muted-foreground hover:text-foreground"
                              onClick={() => void handleToggleRequired(p)}
                              aria-label={p.required === 1 ? 'Mark advisory' : 'Mark required'}
                            >
                              {p.required === 1 ? 'Unrequire' : 'Require'}
                            </button>
                            <button
                              type="button"
                              className="grid size-9 shrink-0 place-items-center text-destructive hover:text-destructive/80"
                              onClick={() => void handleDelete(p.id)}
                              aria-label="Delete policy"
                            >
                              <Trash2 className="size-3.5" />
                            </button>
                          </li>
                        );
                      })
                    )}
                  </ul>
                </section>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
